import type { FilaJukebox } from '../entities/FilaJukebox';
import type { MotivoRejeicao } from '@/shared/lib/jukeboxPedidoError';

export const COOLDOWN_MS              = 30 * 60 * 1_000;
export const MAX_PEDIDOS_SESSAO       = 3;
export const MAX_PEDIDOS_AUTENTICADO  = 5;

export type { MotivoRejeicao };

export interface ResultadoValidacao {
  valido:              boolean;
  motivo?:             MotivoRejeicao;
  cooldownRestanteMs?: number;
}

/**
 * `cooldowns`: Map de video_id → disponivelEm (timestamp absoluto do cliente).
 * O valor é calculado como `Date.now() + restante_ms` no momento em que o
 * servidor retorna o cooldown via RPC, eliminando discrepância de relógio.
 */
export function validarPedido(
  youtubeVideoId: string,
  filaAtiva:      readonly FilaJukebox[],
  cooldowns:      ReadonlyMap<string, number>,
  sessionId:      string,
  isAuthenticated = false,
): ResultadoValidacao {
  const jaEstaFila = filaAtiva.some(
    item =>
      item.youtube_video_id === youtubeVideoId &&
      (item.status === 'pendente' || item.status === 'tocando'),
  );
  if (jaEstaFila) return { valido: false, motivo: 'ja_na_fila' };

  const limite = isAuthenticated ? MAX_PEDIDOS_AUTENTICADO : MAX_PEDIDOS_SESSAO;
  const pedidosAtivos = filaAtiva.filter(
    item =>
      item.session_id === sessionId &&
      (item.status === 'pendente' || item.status === 'tocando'),
  ).length;
  if (pedidosAtivos >= limite) {
    return { valido: false, motivo: 'limite_sessao' };
  }

  const disponivelEm = cooldowns.get(youtubeVideoId);
  if (disponivelEm) {
    const restante = disponivelEm - Date.now();
    if (restante > 0) {
      return { valido: false, motivo: 'em_cooldown', cooldownRestanteMs: restante };
    }
  }

  return { valido: true };
}

/** Retorna ms restantes de cooldown dado o timestamp absoluto de expiração. */
export function cooldownRestanteMs(disponivelEm: number): number {
  return Math.max(0, disponivelEm - Date.now());
}

/**
 * Timestamp absoluto (ms) em que o vídeo volta a poder ser pedido.
 * O relógio começa em `concluido_em` (término/pulo na TV), não no pedido.
 * Sem término — ainda na fila, tocando, ou cancelado sem tocar — não há cooldown.
 */
export function timestampFimCooldown(
  concluidoEm: string | null | undefined,
  agora: number,
): number | null {
  if (!concluidoEm) return null;
  const fim = new Date(concluidoEm).getTime() + COOLDOWN_MS;
  return fim > agora ? fim : null;
}

export function formatarRestante(ms: number): string {
  const min = Math.ceil(ms / 60_000);
  return min <= 1 ? '1 min' : `${min} min`;
}

export type EstadoItem = 'disponivel' | 'fila' | 'minha_fila' | 'tocando' | 'bloqueado';

/**
 * Determina o estado de exibição de um item da playlist no Jukebox.
 * Centraliza a lógica que antes ficava inline no render do JukeboxCliente.
 */
export function calcularEstadoItem(
  videoId:     string,
  filaAtiva:   readonly FilaJukebox[],
  cooldowns:   ReadonlyMap<string, number>,
  tocandoId:   string | null,
  meuSessionId: string,
  agora:       number,
): { estado: EstadoItem; tempoRestante: number } {
  if (videoId === tocandoId) return { estado: 'tocando', tempoRestante: 0 };

  const filaItem = filaAtiva.find(f => f.youtube_video_id === videoId);
  if (filaItem) {
    const estado: EstadoItem = filaItem.session_id === meuSessionId ? 'minha_fila' : 'fila';
    return { estado, tempoRestante: 0 };
  }

  const disponivelEm = cooldowns.get(videoId);
  const restante = disponivelEm ? Math.max(0, disponivelEm - agora) : 0;
  if (restante > 0) return { estado: 'bloqueado', tempoRestante: restante };

  return { estado: 'disponivel', tempoRestante: 0 };
}
